(function(){
  const DOMAIN = window.Shopify.shop;
  const PUBLIC_TOKENS = {
    'loosekid.myshopify.com': 'd181a9adc0ee8fdbdf1d4b54085cba41',
    'loosekid-us.myshopify.com': '80ea1b4db5ef2e49c61301108dc82db0',
    'lskd-sandbox.myshopify.com': '08e89a152cc385e3a831c4d338c5bb73',
  }

  const SORTED_LENGTH = [
    'X Short',
    '4" Short',
    'Mid Short',
    '6" Short',
    'Bike Short',
    '9" Short',
    '3/4 Length',
    '7/8 Length',
    'Full Length',
    'X Long',
    'Tall'
  ];

  class ShopifyStoreFrontAPI {
    constructor() {
      if(typeof ShopifyStorefrontAPIClient === 'undefined') {
          console.error('ShopifyStorefrontAPIClient is not defined');
          return;
      }
      this.client = ShopifyStorefrontAPIClient.createStorefrontApiClient({
        storeDomain: 'https://' + DOMAIN,
        apiVersion: '2024-07',
        publicAccessToken: PUBLIC_TOKENS[DOMAIN]
      })
    }

    /**
     * Fetches products by tag from the Shopify Storefront API and normalises the response for displaying color swatches
     * We keep the sold out products if it has the tag BACK-IN-STOCK
     * The query is sorted by Best Selling descending order
     * we then sort the products so the available products are first, then the sold out (back-in-stock) products
     * We also sort the products by price descending that puts discounted products in the end
     *
     * @param {string} styleHandle - styleHandle tag to filter products.
     * @param {boolean} isOgProduct - If the product is an OG product.
     * @return {Promise<Array<{
     *    id: number
     *    available: boolean
     *    compareAtPrice: number
     *    handle: string
     *    featuredImage: string
     *    swatchColor: string
     *    price: number
     *    tags: Array<string>
     * }>>}
     */
    async getSameStyleProducts(styleHandle, isOgProduct) {
      const selections = `
        id
        title
        handle
        tags
        availableForSale
        compareAtPriceRange {
          minVariantPrice {
            amount
          }
        }
        priceRange {
          minVariantPrice {
            amount
          }
        }
        featuredImage {
          url
        }
      `
      const response = await this.execute(this.queries.productsQuery(selections), { query: `tag:'${styleHandle}' AND ${isOgProduct ? `tag:'batch:OG'` : `NOT tag:'batch:OG'`}` })
      const products = response?.products?.nodes ?? []

      return products
        .filter(product => product.availableForSale || product.tags.includes('BACK-IN-STOCK'))
        .sort((a, b) => {
          // 1. Sort by availability
          if (a.availableForSale !== b.availableForSale) {
            return a.availableForSale ? -1 : 1;
          }

          // 2. Sort by price (higher prices first = less discounted prioritized)
          const aPrice = a.priceRange.minVariantPrice.amount;
          const bPrice = b.priceRange.minVariantPrice.amount;
          const priceDiff = bPrice - aPrice;
          if (bPrice - aPrice !== 0) {
            return priceDiff;
          }

          // 3. Core product ("Black") is prioritized first after availability and price
          const aColor = a.title.split(' - ').pop()?.toLowerCase();
          const bColor = b.title.split(' - ').pop()?.toLowerCase();

          if (aColor === 'black') return -1;
          if (bColor === 'black') return 1;

          // 4. Default case (no change in order)
          return 0;
        })
        .map(product => {
          const swatch = product.tags.find(tag => tag.startsWith('colourSwatch:'))
          let colourNamePrint, swatchColor

          if (swatch) {
            const [_, colour] = swatch.split(':');
            if (colour) {
              const [swatchName, primaryColour, ...secondaryColours] = colour.split('|');
              colourNamePrint = swatchName;
              if (primaryColour) {
                if (primaryColour.startsWith('#')) {
                  const gradientStops = [primaryColour, ...secondaryColours].reverse().map((hexCode, index) => `${hexCode} 0 ${((index + 1) * (100 / (secondaryColours.length + 1)))}%`).join(', ');
                  swatchColor = `conic-gradient(${gradientStops})`;
                } else if (primaryColour.includes('.jpg')) {
                  const image = theme.settings.assetURL.replace('colorplaceholder.jpg', primaryColour);
                  swatchColor = `url(${image}) no-repeat center center / cover`;
                }
              } else {
                swatchColor = swatchName;
              }
            }
          }

          return ({
            id: this.helpers.parseGid(product.id),
            title: product.title,
            compareAtPrice: +(product.compareAtPriceRange?.minVariantPrice?.amount) || 0,
            handle: product.handle,
            featuredImage: product.featuredImage?.url,
            colourNamePrint,
            swatchColor,
            price: +(product.priceRange?.minVariantPrice?.amount) || 0,
            tags: product.tags
          })
        })
    }

    /**
     * Fetches a product by its handle from the Shopify Storefront API.
     *
     * @param {string} handle - The handle of the product to fetch.
     * @return {Promise<Array<{
     *    id: number
     *    available: boolean
     *    dropDateTime: string
     *    pinCode: string
     *    title: string
     *    images: Array<string>
     *    variants: Array<{
     *      available: boolean
     *      id: number
     *      title: string
     *    }>
     * }>|null>}
     */
    async fetchProductByHandle(handle) {
      const response = await this.execute(this.queries.productByHandle, {handle})

      if(!'product' in response) {
        return null
      }

      const { product } = response

      return {
        id: this.helpers.parseGid(product.id),
        title: product.title,
        tags: product.tags,
        images: product.images?.nodes?.map(image => image.url) || [],
        available: product.availableForSale,
        dropDateTime: product.dropDateTime?.value,
        pinCode: product.pinCodeData?.value?.pin_code || null,
        variants: product.variants?.nodes?.map(variant => ({
          available: variant.availableForSale,
          id: this.helpers.parseGid(variant.id),
          title: variant.title
        })) || []
      }
    }

    async getTagContentList() {
      const response = await this.execute(this.queries.tagContentList, {
        handle: {
          handle: "tag-content-list",
          type: "tag_content_list"
        }})

      return this.helpers.transformTagContentList(response)
    }
    /**
     * Fetches products which have a tag that starts with the given `tagGroup` and title that matches `color`.
     *
     * @param {string} tagGroup - The tag group, e.g. 'YGroup_xxx'
     * @param {string} color - The colorSwatch tag.
     * @param {string} optionType - e.g. 'length' or 'cupsize'
     * @param {string} extraGroupTag - e.g. 'pockets:No Pockets'
     * @param {boolean} isOgProduct - If the product is an OG product.
     * @return {Promise<Array<{
     *    id: number
     *    handle: string
     *    title: string
     *    availableForSale: boolean
     *    option: string
     * }>>}
     */
    async fetchProductOptions(tagGroup, color, optionType, extraGroupTag, isOgProduct = false) {
      const selections = `
        id,
        handle,
        title,
        availableForSale,
        tags
      `
      let query = `tag:'${tagGroup}' AND tag:'${color}' AND ${isOgProduct ? `tag:'batch:OG'` : `NOT tag:'batch:OG'`}`
      if (extraGroupTag) {
        query += ` AND tag:'${extraGroupTag}'`
      }
      const response = await this.execute(this.queries.productsQuery(selections), { query })
      return (response?.products?.nodes ?? [])
        .reduce((acc, { id, handle, title, availableForSale, tags }) => {
          const optionTags = tags.filter((tag) => tag.startsWith(`${optionType}:`));
          if (!availableForSale && tags.includes('BACK-IN-STOCK')) return acc;
          if (!optionTags.length) return acc;

          const parsedId = this.helpers.parseGid(id);
          const dimension = this.helpers.getProductDimension(tags, optionType);
          const option = optionTags[0].split(':').at(-1);

          acc.push({ id: parsedId, handle, title, availableForSale, dimension, option });
          return acc;
        }, [])
        // First sort by option name alphabetically for things like cup sizes
        .sort((a, b) => a.option.localeCompare(b.option))
        // Then sort by length if it is part of the SORTED_LENGTH array
        .sort((a, b) => SORTED_LENGTH.indexOf(a.option) - SORTED_LENGTH.indexOf(b.option));
    }

    /**
     * Fetches a variant by selected options from the Shopify Storefront API.
     * @param id - product id
     * @param selectedOptions
     * @returns {Promise<{id: number, available: boolean}|null>}
     */
    async fetchVariantBySelectedOptions(id, selectedOptions) {
      const response = await this.execute(this.queries.variantBySelectedOptions, { id: `gid://shopify/Product/${id}`, selectedOptions });
      const variant = response?.product?.variantBySelectedOptions;
      return variant ? {
        id: this.helpers.parseGid(variant.id),
        available: variant.availableForSale,
        sku: variant.sku,
        inventory_management: 'shopify',
        price: Number(variant.price.amount) * 100,
        compare_at_price: variant.compareAtPrice ? Number(variant.compareAtPrice.amount) * 100 : null,
        options: variant.selectedOptions.map(({ value }) => (value)),
        option1: variant.selectedOptions[0]?.value || null,
        option2: variant.selectedOptions[1]?.value || null,
        option3: variant.selectedOptions[2]?.value || null,
      } : null;
    }

    async execute(query, variables) {
      if(typeof ShopifyStorefrontAPIClient === 'undefined') {
          console.error('ShopifyStorefrontAPIClient is not defined');
          return;
      }
      const { data, errors } = await this.client.request(query, {
        variables : {
          ...variables,
          countryCode: this.helpers.getCountryCode()
        }
      });

      errors && console.error('ShopifyStorefrontAPIClient ERRORS', errors);

      return data
    }

    queries = {
      productsQuery: (selections) => `
        query productsQuery($query: String!, $countryCode: CountryCode) @inContext(country: $countryCode){
          products(
            query: $query
            first: 50
            sortKey: CREATED_AT
            reverse: true
          ) {
            nodes {
              ${selections}
            }
          }
        }
      `,
      productByHandle: `
        query getProductByHandle($handle: String!, $countryCode: CountryCode) @inContext(country: $countryCode){
          product(handle: $handle) {
            id
            title
            availableForSale
            tags
            dropDateTime: metafield(key: "datetime", namespace: "drop") {
              value
            }
            pinCodeData: metafield(key: "data", namespace: "drop") {
              value
            }
            images(first: 6) {
              nodes {
                url
              }
            }
            variants(first: 20) {
              nodes {
                availableForSale
                id
                title
              }
            }
          }
        }
      `,
      tagContentList: `
        query getTagContentList($handle: MetaobjectHandleInput!, $countryCode: CountryCode) @inContext(country: $countryCode){
          metaobject(handle: $handle) {
            field(key: "contents") {
              references(first: 100) {
                nodes {
                  ... on Metaobject {
                    tag: field(key: "tag") {
                      value
                    }
                    exclude_tags: field(key: "exclude_tags") {
                      value
                    }
                    markets: field(key: "markets") {
                      value
                    }
                    pdp_above_atc: field(key: "pdp_above_atc") {
                      references(first: 10) {
                        nodes {
                          ...Fields
                        }
                      }
                    }
                    pdp_above_description: field(key: "pdp_above_description") {
                      references(first: 10) {
                        nodes {
                          ...Fields
                        }
                      }
                    }
                    pdp_above_title: field(key: "pdp_above_title") {
                      references(first: 10) {
                        nodes {
                          ...Fields
                        }
                      }
                    }
                    pdp_below_atc: field(key: "pdp_below_atc") {
                      references(first: 10) {
                        nodes {
                          ...Fields
                        }
                      }
                    }
                    pdp_below_price: field(key: "pdp_below_price") {
                      references(first: 10) {
                        nodes {
                          ...Fields
                        }
                      }
                    }
                    pdp_above_review: field(key: "pdp_above_review") {
                      references(first: 10) {
                        nodes {
                          ...Fields
                        }
                      }
                    }
                    pdp_below_review: field(key: "pdp_below_review") {
                      references(first: 10) {
                        nodes {
                          ...Fields
                        }
                      }
                    }
                    plp_below: field(key: "plp_below") {
                      references(first: 10) {
                        nodes {
                          ...Fields
                        }
                      }
                    }
                    plp_bottom_left: field(key: "plp_bottom_left") {
                      references(first: 10) {
                        nodes {
                          ...Fields
                        }
                      }
                    }
                    plp_top_left: field(key: "plp_top_left") {
                      references(first: 10) {
                        nodes {
                          ...Fields
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }

        fragment Fields on Metaobject {
          id
          type
          fields {
            key
            value
            imageData: reference {
              ... on MediaImage {
                image {
                  altText
                  url
                  width
                  height
                }
              }
            }
          }
        }
      `,
      variantBySelectedOptions: `
        query getVariantBySelectedOptions($id: ID!, $selectedOptions: [SelectedOptionInput!]!, $countryCode: CountryCode) @inContext(country: $countryCode){
          product(id: $id) {
            variantBySelectedOptions(selectedOptions: $selectedOptions) {
              id
              availableForSale
              sku
              price {
                amount
              }
              compareAtPrice {
                amount
              }
              selectedOptions {
                name
                value
              }
            }
          }
        }
      `
    }

    helpers = {
      parseGid: (gId) => +(gId?.split('/').pop()) || null,
      getCountryCode: () => window.Shopify?.country ?? 'AU',
      /**
       * Transforms the tag content list response from array to a  key value object with flattened sections contents
       */
      transformTagContentList: (data) => (data?.metaobject?.field?.references?.nodes ?? []).reduce((acc, node, index) => {
        if (!node.tag?.value) return acc;
        const { tag, exclude_tags, markets, ...sectionsContents } = node;
        // Filter out the tags that are not available in the current market
        const availableMarkets = markets?.value ? JSON.parse(markets.value) : null;
        if (
          availableMarkets &&
          !(
            availableMarkets.includes(window.Shopify?.country) ||
            (theme.settings.isMarketROW && availableMarkets.includes('ROW'))
          )
        ) return acc;

        return {
          ...acc,
          [tag.value]: {
            sortOrder: index,
            excludeTags: exclude_tags ? JSON.parse(exclude_tags.value) : [],
            ...this.helpers.flattenSectionsContents(sectionsContents)
          }
        };
      }, {}),
      /**
       * Flattens the sections contents to a single level object
       */
      flattenSectionsContents: (sectionsContents) => Object.keys(sectionsContents).reduce((acc, key) => ({
        ...acc,
        [key]: sectionsContents[key]?.references?.nodes ?? []
      }), {}),

      getProductDimension: (tags, optionType) => {
        const tag = tags.find((tag) => tag.startsWith(`dimensions_${optionType}:`))
        return tag ? tag.split(`dimensions_${optionType}:`).pop() : ''
      }
    }
  }

  (window.ShopifyStoreFrontAPI = new ShopifyStoreFrontAPI()).getTagContentList().then((tagContentList) => {
    window.getTagsContents = (tags) => tags
      .reduce((acc, tag) => {
        if (tagContentList[tag]) {
          acc.push(tagContentList[tag]);
        }
        return acc;
      }, [])
      .filter(({ excludeTags }) => !excludeTags || !excludeTags.some((excludedTag) => tags.includes(excludedTag)))
      .sort(({ sortOrder: a }, { sortOrder: b }) => a - b);

    // console.info("%c✅ Tag Content Loaded", "color:#48DA6EFF; font-size:16px;");
    document.dispatchEvent(new CustomEvent("TagContentLoaded"));
  });
})()
